import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { marketplaceService } from "@/services/marketplace/MarketplaceService";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { buildCobrancaConsultaNotification } from "@/services/notification/notificationHelpers";
import { notificationService } from "@/services/notification/PostNotification";
import type { PaymentDetailData } from "../types/consultationsBilling.types";
import {
  isPaymentChargeNotificationSent,
  markPaymentChargeNotificationSent,
  resolveChargeNotificationContext,
} from "../utils/matchConsultationToPayment";

interface UseSendBillingChargeOptions {
  linkedPendingAppointments: fhir5.Appointment[];
  onSuccess?: () => void | Promise<void>;
}

async function sendSingleCharge(
  chargePayment: PaymentDetailData,
  linkedPendingAppointments: fhir5.Appointment[],
  organizationId?: string,
) {
  try {
    const phoneNumber = chargePayment.patient?.telecom?.find(
      (entry) => entry.system === "phone"
    )?.value;
    if (!phoneNumber) {
      throw new Error(
        `Paciente ${chargePayment.patient?.name?.[0]?.text} sem telefone cadastrado`
      );
    }
    const { healthcareDisplay, practitionerName } =
      resolveChargeNotificationContext(chargePayment);
    const paymentLink = await marketplaceService.sendPaymentMessage({
      slotReference: chargePayment.payment.request?.reference || "",
      patientName: chargePayment.patient?.name?.[0]?.text || "",
      healthcareDisplay,
      practitionerName,
      phoneNumber,
    });
    await notificationService.sendNotification(
      buildCobrancaConsultaNotification({
        cpf: chargePayment.patient?.identifier?.[0]?.value || "",
        nomePaciente: chargePayment.patient?.name?.[0]?.text || "",
        tipoConsulta: healthcareDisplay,
        nomeProfissional: practitionerName,
        linkPagamento: paymentLink,
        organizationId,
      }),
      { organizationId },
    );
    await markPaymentChargeNotificationSent(chargePayment, {
      appointments: linkedPendingAppointments,
    });
    return {
      success: true,
      patientName: chargePayment.patient?.name?.[0]?.text,
    };
  } catch (error: any) {
    return {
      success: false,
      patientName: chargePayment.patient?.name?.[0]?.text,
      error:
        error?.response?.status === 404
          ? "Agenda sem custo"
          : error.message ===
              `Paciente ${chargePayment.patient?.name?.[0]?.text} sem telefone cadastrado`
            ? error.message
            : "Erro ao gerar link de pagamento",
    };
  }
}

export function useSendBillingCharge({
  linkedPendingAppointments,
  onSuccess,
}: UseSendBillingChargeOptions) {
  const { organizationId } = useOrganizationContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const isChargeSent = useCallback(
    (payment: PaymentDetailData) =>
      isPaymentChargeNotificationSent(payment, {
        appointments: linkedPendingAppointments,
      }),
    [linkedPendingAppointments]
  );

  const sendCharges = useCallback(
    async (paymentsToProcess: PaymentDetailData[]) => {
      setLoading(true);
      setError(undefined);
      try {
        const results = await Promise.all(
          paymentsToProcess.map((payment) =>
            sendSingleCharge(payment, linkedPendingAppointments, organizationId)
          )
        );
        const successful = results.filter((result) => result.success);
        const failed = results.filter((result) => !result.success);
        if (successful.length > 0) {
          toast.success(
            `Cobranças enviadas com sucesso para ${successful.length} paciente(s)`
          );
          await onSuccess?.();
        }
        if (failed.length > 0) {
          toast.error(
            failed.map((entry) => `${entry.patientName}: ${entry.error}`).join("\n")
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [linkedPendingAppointments, onSuccess, organizationId]
  );

  return {
    loading,
    error,
    setError,
    isChargeSent,
    sendCharges,
  };
}
